import React from 'react';
import { useQuery } from 'react-query';
import api from '../../services/api';
import Loading from '../../components/common/Loading';
import { 
  FaChartLine, 
  FaChartBar, 
  FaChartPie, 
  FaDollarSign,
  FaShoppingCart,
  FaUsers,
  FaBox
} from 'react-icons/fa';

const Analytics = () => {
  const { data: analyticsData, isLoading, error } = useQuery(
    'adminAnalytics',
    () => api.get('/admin/analytics'),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  if (isLoading) return <Loading />;
  if (error) return <div className="text-center p-8">Error loading analytics data</div>;

  const analytics = analyticsData?.data || {};

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Analytics</h1>
          <p className="text-gray-600">Business insights and performance metrics</p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100 text-blue-600">
                <FaDollarSign className="text-2xl" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                <p className="text-2xl font-semibold text-gray-900">
                  ${analytics.totalRevenue?.toFixed(2) || '0.00'}
                </p>
                <p className="text-sm text-green-600">
                  +{analytics.revenueGrowth || 0}% from last month
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100 text-green-600">
                <FaShoppingCart className="text-2xl" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Orders</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {analytics.totalOrders || 0}
                </p>
                <p className="text-sm text-green-600">
                  +{analytics.orderGrowth || 0}% from last month
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100 text-purple-600">
                <FaUsers className="text-2xl" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Customers</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {analytics.totalCustomers || 0}
                </p>
                <p className="text-sm text-green-600">
                  +{analytics.customerGrowth || 0}% from last month
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-yellow-100 text-yellow-600">
                <FaBox className="text-2xl" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Products</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {analytics.totalProducts || 0}
                </p>
                <p className="text-sm text-green-600">
                  +{analytics.productGrowth || 0}% from last month
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Sales Chart */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <FaChartLine className="mr-2" />
              Sales Overview
            </h2>
            <div className="h-64 flex items-center justify-center bg-gray-50 rounded">
              <p className="text-gray-500">Sales chart will be implemented with Chart.js or Recharts</p>
            </div>
          </div>

          {/* Top Products */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <FaChartBar className="mr-2" />
              Top Selling Products
            </h2>
            {analytics.topProducts && analytics.topProducts.length > 0 ? (
              <div className="space-y-4">
                {analytics.topProducts.slice(0, 5).map((product, index) => (
                  <div key={product._id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-500 w-6">#{index + 1}</span>
                      <img
                        src={product.images?.[0] || 'https://via.placeholder.com/30x30?text=No+Image'}
                        alt={product.name}
                        className="w-8 h-8 object-cover rounded mr-3"
                      />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{product.name}</p>
                        <p className="text-xs text-gray-500">{product.category}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-gray-900">
                        {product.salesCount} sold
                      </p>
                      <p className="text-xs text-gray-500">
                        ${product.totalRevenue?.toFixed(2)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No sales data available</p>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="mt-8 bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Activity</h2>
          {analytics.recentActivity && analytics.recentActivity.length > 0 ? (
            <div className="space-y-4">
              {analytics.recentActivity.map((activity, index) => (
                <div key={index} className="flex items-center p-4 bg-gray-50 rounded">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <FaShoppingCart className="text-blue-600 text-sm" />
                    </div>
                  </div>
                  <div className="ml-4 flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {activity.description}
                    </p>
                    <p className="text-sm text-gray-500">
                      {new Date(activity.timestamp).toLocaleString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-gray-900">
                      {activity.amount ? `$${activity.amount.toFixed(2)}` : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No recent activity</p>
          )}
        </div>

        {/* Performance Metrics */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Conversion Rate</h3>
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-600">
                {analytics.conversionRate?.toFixed(1) || 0}%
              </p>
              <p className="text-sm text-gray-500 mt-2">Visitors to customers</p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Average Order Value</h3>
            <div className="text-center">
              <p className="text-3xl font-bold text-green-600">
                ${analytics.averageOrderValue?.toFixed(2) || '0.00'}
              </p>
              <p className="text-sm text-gray-500 mt-2">Per order</p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Retention</h3>
            <div className="text-center">
              <p className="text-3xl font-bold text-purple-600">
                {analytics.customerRetention?.toFixed(1) || 0}%
              </p>
              <p className="text-sm text-gray-500 mt-2">Returning customers</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics; 